import Script from 'next/script'
import type { RegistrationSource } from '@repo/auth-utils'

import { initScript as bingInitScript } from '@repo/analytics/bing'

import {
  stubScript as facebookPixelStubScript,
  scriptUrl as facebookPixelScriptUrl,
  FacebookPixel as FacebookPixelImage,
} from '@repo/analytics/facebook'
import { IubendaSnippet } from './iubenda-snippet'
import { OpenAiPixel } from './openai'
import { GlobalSiteTag } from './global-site-tag'
import { GlobalOneTapLogin } from './global-one-tap-login'
import { Clarity } from './clarity'
import { IubendaScripts, useIubendaConfig } from './iubenda'

const Bing = () => {
  const { gdprApplies, usprApplies, scriptProps, variant } = useIubendaConfig()

  if (variant === 'locationNotSet') {
    return null
  }

  return gdprApplies || usprApplies ? (
    <IubendaSnippet
      purposes={scriptProps['data-iub-purposes']}
      defer={scriptProps['data-cf-defer']}
      id="bing-script"
    >
      {bingInitScript}
    </IubendaSnippet>
  ) : (
    <Script id="bing-script" strategy="lazyOnload" {...scriptProps}>
      {bingInitScript}
    </Script>
  )
}

const FacebookPixel = () => {
  const { gdprApplies, usprApplies, scriptProps, variant } = useIubendaConfig()

  if (variant === 'locationNotSet') {
    return null
  }

  return (
    <>
      {/* Queue stub runs early so events fired before fbevents.js loads are
          queued instead of dropped; the heavy script itself stays deferred. */}
      {gdprApplies || usprApplies ? (
        <IubendaSnippet
          purposes={scriptProps['data-iub-purposes']}
          defer={scriptProps['data-cf-defer']}
          id="facebook-pixel-stub"
        >
          {facebookPixelStubScript}
        </IubendaSnippet>
      ) : (
        <Script id="facebook-pixel-stub" strategy="afterInteractive" {...scriptProps}>
          {facebookPixelStubScript}
        </Script>
      )}
      {gdprApplies || usprApplies ? (
        <IubendaSnippet
          purposes={scriptProps['data-iub-purposes']}
          defer={scriptProps['data-cf-defer']}
          id="facebook-pixel-script"
          src={facebookPixelScriptUrl}
        />
      ) : (
        <Script
          id="facebook-pixel-script"
          src={facebookPixelScriptUrl}
          strategy="lazyOnload"
          {...scriptProps}
        />
      )}
      <FacebookPixelImage />
    </>
  )
}

interface AnalyticsProps {
  userId?: string
  autoLogin?: boolean
  registrationSource?: RegistrationSource
}

export const Analytics = ({ userId, autoLogin, registrationSource }: AnalyticsProps = {}) => {
  return (
    <>
      <GlobalOneTapLogin autoSelect={autoLogin} registrationSource={registrationSource} />
      <GlobalSiteTag />
      <FacebookPixel />
      <Bing />
      <OpenAiPixel />
      <Clarity userId={userId} />
      <IubendaScripts />
    </>
  )
}
